import {
    FetchOrgSummaryViewSuccessAction,
    FETCH_ORG_SUMMARY_VIEW_SUCCESS,
    OrgSummaryViewResponse,
} from '../actions/orgSummaryView';
import {
    FetchOrgDestinationsAndVersionsSuccessAction,
    FETCH_ORG_DESTINATIONS_AND_VERSIONS_SUCCESS,
} from '../actions/orgDestinationsAndVersions';
import {
    SummaryViewDataRowAPI,
    SummaryViewResponseAPI,
    VersionSummaryViewDataItemAPI,
    OrgDestinationsResponse,
} from '../types/summaryView';
import { LogoutSuccessAction, LOGOUT_SUCCESS } from '../actions/login';
import { combineReducers } from 'redux';
import { ClearStateAction, CLEAR_STATE } from '../actions/state';

type SummaryViewsActionTypes =
    | LogoutSuccessAction
    | FetchOrgSummaryViewSuccessAction
    | FetchOrgDestinationsAndVersionsSuccessAction
    | ClearStateAction;

function buildVersionDestinationKey(item: VersionSummaryViewDataItemAPI) {
    return `${item.version}::${item.destinationId}`;
}

function mergeRows(existingRows: SummaryViewDataRowAPI[] = [], incomingRows: SummaryViewDataRowAPI[] = []) {
    const rowMap = new Map<string, SummaryViewDataRowAPI>();
    existingRows.forEach((row) => {
        rowMap.set(row.type, row);
    });
    incomingRows.forEach((row) => {
        rowMap.set(row.type, row);
    });
    return Array.from(rowMap.values());
}

function mergeSummaryItems(
    existingItems: VersionSummaryViewDataItemAPI[] = [],
    incomingItems: VersionSummaryViewDataItemAPI[] = [],
) {
    const itemMap = new Map<string, VersionSummaryViewDataItemAPI>();

    existingItems.forEach((item) => {
        itemMap.set(buildVersionDestinationKey(item), item);
    });

    incomingItems.forEach((incoming) => {
        const key = buildVersionDestinationKey(incoming);
        const existing = itemMap.get(key);

        if (existing) {
            itemMap.set(key, {
                ...existing,
                ...incoming,
                rows: mergeRows(existing.rows, incoming.rows),
            });
        } else {
            itemMap.set(key, incoming);
        }
    });

    return Array.from(itemMap.values());
}

function mergeSummaryView(existing: SummaryViewResponseAPI | undefined, incoming: SummaryViewResponseAPI) {
    if (!existing) {
        return incoming;
    }

    return {
        ...existing,
        ...incoming,
        versionSummaryViewItems: mergeSummaryItems(existing.versionSummaryViewItems, incoming.versionSummaryViewItems),
    };
}

function updateSummaryViewsById(state: SummaryViewsById, payload: OrgSummaryViewResponse) {
    const existingSummaryView = state[payload.requestId];
    const mergedSummaryView = mergeSummaryView(existingSummaryView, payload.summaryView);

    return {
        ...state,
        [payload.requestId]: mergedSummaryView,
    };
}

type SummaryViewsById = Record<string, SummaryViewResponseAPI>;

function summaryViewsById(state: SummaryViewsById = {}, action: SummaryViewsActionTypes): SummaryViewsById {
    switch (action.type) {
        case FETCH_ORG_SUMMARY_VIEW_SUCCESS:
            return updateSummaryViewsById(state, action.payload);
        case LOGOUT_SUCCESS:
        case CLEAR_STATE:
            return {};
        default:
            return state;
    }
}

type OrgDestinationsByOrgId = Record<string, OrgDestinationsResponse>;

function orgDestinationsAndVersions(
    state: OrgDestinationsByOrgId = {},
    action: SummaryViewsActionTypes,
): OrgDestinationsByOrgId {
    switch (action.type) {
        case FETCH_ORG_DESTINATIONS_AND_VERSIONS_SUCCESS:
            return {
                ...state,
                [action.payload.orgId]: action.payload.data,
            };
        case LOGOUT_SUCCESS:
        case CLEAR_STATE:
            return {};
        default:
            return state;
    }
}

const summaryViewsReducer = combineReducers({
    byId: summaryViewsById,
    orgDestinations: orgDestinationsAndVersions,
});

export default summaryViewsReducer;
